import { EllipsedText, FlexColumn, FlexRow, MaterialIcon, StyledButton } from '@gorila-shared-ui/components';
import { LabelMedium, ParagraphSmall } from 'baseui/typography';
import { memo } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { SEND_COMMAND_ACTION } from '../../constants/app';
import { ASSET_TYPE_PHONE } from '../../constants/asset';
import { useStyles } from '../../hooks/useStyles';
import { availableActionsState } from '../../storage/app';
import { selectedCurrentStateActionState } from '../../storage/client';
import { CurrentState, CurrentStateAction } from '../../types/currentState';
import { getAssetTitle } from '../../utils/assets';
import { deviceIsCamera } from '../../utils/currentStates';
import ConsumptionLabel from '../shared/ConsumptionLabel';
import OperationalStatusLabel from '../shared/OperationalStatusLabel';
import OperationalStatusTime from '../shared/OperationalStatusTime';
import { StyledTooltip } from '../ui/StyledTooltip';

type Props = {
  currentState: CurrentState;
};

function CurrentStateBasicHeader({ currentState }: Readonly<Props>) {
  const { theme, css, classes } = useStyles();
  const tooltipPlacement = 'bottom';
  const actions = useRecoilValue(availableActionsState);
  const setAction = useSetRecoilState(selectedCurrentStateActionState);
  const canSendCommand = actions.includes(SEND_COMMAND_ACTION) && currentState.asset.assetType !== ASSET_TYPE_PHONE;

  const openCommands = () => {
    if (canSendCommand) {
      setAction({ ident: currentState.ident, name: CurrentStateAction.command });
    }
  };

  return (
    <FlexColumn gap={theme.sizing.scale100}>
      <FlexRow
        classNames={css({
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 0,
          overflow: 'hidden',
        })}
      >
        <FlexColumn gap={0}>
          <FlexRow gap={theme.sizing.scale200}>
            <LabelMedium>
              <EllipsedText
                breakAll
                inline
              >
                {getAssetTitle(currentState.asset)}
              </EllipsedText>
            </LabelMedium>
          </FlexRow>
          <StyledTooltip
            content="Etiqueta"
            placement={tooltipPlacement}
          >
            <ParagraphSmall
              margin={0}
              color={theme.colors.contentSecondary}
            >
              <EllipsedText
                breakAll
                inline
              >
                {`(${currentState.asset.label})`}
              </EllipsedText>
            </ParagraphSmall>
          </StyledTooltip>
        </FlexColumn>
        {canSendCommand && (
          <FlexRow
            gap={theme.sizing.scale300}
            classNames={classes.centeredEnd}
          >
            <StyledButton
              kind="tertiary"
              onClick={openCommands}
              shape="pill"
              startEnhancer={
                <MaterialIcon
                  name="settings_remote"
                  size={theme.sizing.scale600}
                />
              }
            >
              Enviar comando
            </StyledButton>
          </FlexRow>
        )}
      </FlexRow>
      <FlexRow gap={theme.sizing.scale300}>
        {deviceIsCamera(currentState) && (
          <ConsumptionLabel
            gbConsumed={currentState.asset.gbConsumed}
            percentageConsumed={currentState.asset.gbAssetConsumption}
            fillWidth
            withVerticalWhiteSpace
          />
        )}
        {currentState.operationalState && (
          <OperationalStatusTime
            operationalState={currentState.operationalState}
            fillWidth
            withVerticalWhiteSpace
          />
        )}
        {currentState.operationalState && (
          <OperationalStatusLabel
            operationalState={currentState.operationalState}
            positionDirection={currentState.position_direction}
            speed={currentState.position_speed}
            sleepMode={currentState.sleepModeStatus}
            fillWidth
            withVerticalWhiteSpace
          />
        )}
      </FlexRow>
    </FlexColumn>
  );
}

export default memo(CurrentStateBasicHeader);
